import * as React from 'react';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Eye as EyeIcon } from '@phosphor-icons/react/dist/ssr/Eye';
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X';

import api from '@src/api/clients/default';

// ─── Context ────────────────────────────────────────────────────────

const ClientViewContext = React.createContext({
  viewAsClient: null,
  setViewAsClient: () => {},
});

export function useClientView() {
  return React.useContext(ClientViewContext);
}

export function ClientViewProvider({ children }) {
  const [viewAsClient, setViewAsClient] = React.useState(null);

  const value = React.useMemo(
    () => ({ viewAsClient, setViewAsClient }),
    [viewAsClient]
  );

  return (
    <ClientViewContext.Provider value={value}>
      {children}
    </ClientViewContext.Provider>
  );
}

// ─── Switcher Button (staff only) ───────────────────────────────────

export default function ClientViewSwitcher() {
  const { viewAsClient, setViewAsClient } = useClientView();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    if (!clients.length) loadClients();
  };

  const handleClose = () => setAnchorEl(null);

  const loadClients = async () => {
    setLoading(true);
    try {
      const res = await api.request({
        url: 'dashboard/clients/?fields=id,name,slug&active=true',
        method: 'GET',
      });
      const rows = Array.isArray(res?.data?.results)
        ? res.data.results
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setClients(rows);
    } catch {
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  if (viewAsClient) {
    return (
      <Chip
        icon={<EyeIcon size={16} />}
        label={`Viewing as: ${viewAsClient.name}`}
        color="warning"
        size="small"
        onDelete={() => setViewAsClient(null)}
        deleteIcon={<XIcon size={14} />}
        sx={{ fontWeight: 600 }}
      />
    );
  }

  return (
    <>
      <Tooltip title="View as Client">
        <IconButton onClick={handleOpen} size="small">
          <EyeIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            View as Client Member
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            See the app as a client member would. Select a client to switch views.
          </Typography>
          <Autocomplete
            options={clients}
            getOptionLabel={(o) => o.name || ''}
            loading={loading}
            onChange={(_, value) => {
              if (value) {
                setViewAsClient(value);
                handleClose();
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Client" size="small" autoFocus />
            )}
            size="small"
          />
        </Box>
      </Popover>
    </>
  );
}
